import { AxiosError } from 'axios';
import { FULFILLMENT_TYPE, STATUS_DESCRIPTION } from '../../constants/status';
import { FeatureSwitches } from '../../constants/feature-switches';
import { isCashNCarryOrder } from '../../components/shared/order-number-formatter/OrderNumberFormatter';
import { IOrderLine, TransactionType } from '../../models/soris';
import {
  IErrorOMSService,
  IErrorReturnService,
  IGenericError,
  IGenericOMSError,
  ILegacyNestedError,
} from '../../models/returns/returns-common';
import { IDeliveryTrackerData, ILineItem } from '../../models/common';
import {
  IOrderDetailsResponse,
  IFulfillmentGroup,
} from '../../models/order-details';
import {
  OrderDetailsLineItemSessionObj,
  OrderDetailsSessionDataV2,
  FGShipment,
  ShipmentInfoSessionObj,
  ShippingInfoDict,
} from '../../models/returns';
import { getOrderStatus } from '../../components/order-details/util/FulfillmentStatus';

const buildOrderLevelLineItemsSessionObj = (
  fulfillmentGroups: IFulfillmentGroup[],
  allowUndelivered: boolean,
): OrderDetailsLineItemSessionObj[] => {
  const lineItems: ILineItem[] = fulfillmentGroups.flatMap(
    (group) => group.lineItems || [],
  );
  return lineItems.map(
    (lineItem): OrderDetailsLineItemSessionObj => ({
      lineId: lineItem.lineId || '',
      omsId: lineItem.omsId,
      secOmsId: lineItem.secOmsId,
      relationType: lineItem.parentLine?.relationType,
      chargeIdentifier: lineItem.chargeIdentifier,
      skuNumber: lineItem.skuNumber,
      imageUrl: lineItem.imageUrl,
      brandName: lineItem.brandName,
      description: lineItem.description,
      originalOrderedQuantity:
        lineItem.originalOrderedQuantity ?? lineItem?.orginalOrderedQuantity,
      currentQuantity: lineItem.currentQuantity,
      unitPrice: lineItem.unitPrice || 0,
      unitCost: lineItem.unitCost,
      totalPrice: lineItem.totalPrice,
      departmentNumber: lineItem.departmentNumber || 0,
      classId: lineItem.classId || 0,
      subClassId: lineItem.subClassId || 0,
      upcCode: lineItem.upcCode,
      itemWeight: lineItem.itemWeight || 0,
      PONumber: lineItem.PONumber,
      vendorNumber: lineItem.vendorNumber,
      isDFCSourced: lineItem.isDFCSourced,
      vendorName: lineItem.vendorName,
      vendorPartNumber: lineItem.vendorPartNumber,
      fulfillmentVendorNumber: lineItem.fulfillmentVendorNumber,
      returnOnlineAllowed: lineItem.actions?.returnOnlineAllowed,
      returnDropoffAllowed: lineItem.actions?.returnDropoffAllowed,
      returnInStoreAllowed: lineItem.actions?.returnInStoreAllowed,
      returnLORPAllowed: lineItem.actions?.returnLORPAllowed,
      productType: lineItem.productType,
      returnWindowEndDate: lineItem.returnWindowEndDate,
      isStatusDeliveredOrOrderPickedup: allowUndelivered
        ? true
        : getOrderStatus(lineItem?.statusCode || 101)
            .isStatusDeliveredOrOrderPickedup,
      customBlindProductType: lineItem.customBlindProductType,
      scac: lineItem.scac,
      shippingCharge: lineItem.shippingCharge,
    }),
  );
};

const buildShippingDictionary = (
  fulfillmentGroups: IFulfillmentGroup[],
): ShippingInfoDict => {
  const shippingAddresses: ShippingInfoDict = {};
  fulfillmentGroups.forEach((group) => {
    group.lineItems?.forEach((item) => {
      if (item.lineId) {
        shippingAddresses[item.lineId] = {
          shippingAddress: group.shippingAndHandling?.deliveryAddress,
          fulfillmentType: group.fulfillmentType,
        };
      }
    });
  });
  return shippingAddresses;
};

/**
 * Takes OMS response to map fields to sessionStorage for later API use
 * @param orderDetailsData original OMS response
 * @param fulfillmentGroup list of items ordered for this fulfillment, only items for this fulfillment should be stored in session for return process, not all the items within the order
 */
export const buildOrderDetailsV2SessionObj = (
  orderDetailsData: IOrderDetailsResponse,
  allowUndelivered: boolean,
  isCashNCarryOrderNumber = false,
): OrderDetailsSessionDataV2 => ({
  storeNumber: orderDetailsData.storeNumber ?? 0,
  orderNumber: orderDetailsData.orderNumber || '',
  grandTotalAmount: orderDetailsData.grandTotalAmount,
  subTotalAmount: orderDetailsData.subTotalAmount,
  shippingCharge: orderDetailsData.shippingCharge,
  deliveryCharge: orderDetailsData.deliveryCharge,
  emailId: orderDetailsData.emailId,
  tenders: orderDetailsData.tenders,
  createDateTime: orderDetailsData.createDateTime,
  lineItems: buildOrderLevelLineItemsSessionObj(
    orderDetailsData.fulfillmentGroups,
    allowUndelivered,
  ),
  chargeTransactions: orderDetailsData.chargeTransactions ?? [],
  billingAddress: orderDetailsData.billingAddress,
  versionId: orderDetailsData.versionId,
  fulfillmentGroups: orderDetailsData.fulfillmentGroups,
  deliveryDictionary: buildShippingDictionary(
    orderDetailsData.fulfillmentGroups,
  ),
  paymentMethods: orderDetailsData.paymentMethods,
  net: orderDetailsData.net,
  isInStoreOrder: isCashNCarryOrder(
    orderDetailsData.orderNumber,
    orderDetailsData.orderOrigin,
  ),
  returnOnlineAllowed: orderDetailsData.actions?.returnOnlineAllowed,
  returnDropoffAllowed: orderDetailsData.actions?.returnDropoffAllowed,
  returnInStoreAllowed: orderDetailsData.actions?.returnInStoreAllowed,
  returnLORPAllowed: orderDetailsData.actions?.returnLORPAllowed,
  orderShipmentsInfo: createShippingsMap(orderDetailsData),
  isCashNCarryOrderNumber,
});

/**
 * Safely checks both the Legacy Nested Error code and the new Generic Error code and returns the error code.
 * @param error - The Axios error object.
 * @returns Returns the object inside the error (code, description and retryable) If none found, will return undefined
 */
export const getAPIErrorData = (
  error: AxiosError<ILegacyNestedError | IGenericError | IGenericOMSError>,
): IErrorReturnService | IErrorOMSService | undefined => {
  const errorData =
    (error?.response?.data as IGenericError)?.errors?.[0] ||
    (error?.response?.data as ILegacyNestedError)?.returnService?.errorData
      ?.errors?.error?.[0] ||
    (error?.response?.data as IGenericOMSError)?.error?.[0] ||
    undefined;
  return errorData;
};

export const buildSorisOrderLineList = (
  lineItems: ILineItem[] = [],
): IOrderLine[] =>
  lineItems.map((lineItem) => ({
    lineItemId: lineItem.lineId || '',
    quantity: lineItem.currentQuantity?.toString() || '',
    channelIdentifier: lineItem.channelIdentifier || '',
  }));

export const buildReturnLabelRequest = (
  orderId: string,
  emailId: string,
  trackingNumbers: string[],
  isGuest: boolean,
  net: string | undefined,
) => {
  return {
    PrintReturnShippingLabelRequest: {
      EmailId: emailId,
      OrderId: orderId,
      TrackingNumbers: trackingNumbers,
    },
    emailId,
    orderId,
    isGuest,
    net: net || '',
    trackingNumbers,
  };
};

export const retrieveTrackingNumbers = (fg: IFulfillmentGroup): string[] => {
  // Take all unique tracking numbers from every lineItems's returnOrderTrackingInfo list in the fulfillment group
  const trackingNumbersSet = new Set<string>();
  fg?.lineItems?.forEach((lineItem) => {
    lineItem?.returnOrderTrackingInfo?.forEach((trackingInfo) => {
      // TrackingNumber can be omitted by the API; guard so we never add undefined and
      // break the string[] contract downstream.
      if (trackingInfo?.TrackingNumber) {
        trackingNumbersSet.add(trackingInfo.TrackingNumber);
      }
    });
  });
  // Simplifying trackingNumbers interface from IHDTrackingInfo[] to string[]
  //  since we only need the tracking number for now
  return Array.from(trackingNumbersSet);
};

/**
 * Returns a string with either 'nex' or 'aafes' depending on the order's militaryExchange, empty otherwise
 *  */
export const getMilitaryExchange = (
  orderDetailsData: IOrderDetailsResponse,
) => {
  if (orderDetailsData.isNEXOrder) return 'nex';
  if (orderDetailsData.isAAFESOrder) return 'aafes';
  return '';
};

/**
 * will generate a mapping that will be used to determine if # of boxes shipped are known
 * currently only supported by DFC orders
 * @param orderDetailsV2 orders object
 * @returns lineId key -> FG.shippingAndHandling.shipments.shipment mapping
 */
export const createShippingsMap = (
  orderDetailsData?: IOrderDetailsResponse,
): ShipmentInfoSessionObj => {
  const shipmentsArray: FGShipment[] = [];
  const itemShipmentsDictionary: Record<string, string> = {};

  orderDetailsData?.fulfillmentGroups?.forEach((fg) => {
    // add fg shipments to the array
    shipmentsArray.push({
      shipment: fg.shippingAndHandling?.shipments?.shipment,
      isShippedBoxesKnown: fg.isShippedBoxesKnown,
    });

    fg.lineItems?.forEach((item) => {
      const itemId = item.lineId;
      if (!itemId) return;
      // add itemId to shipment mapping
      itemShipmentsDictionary[itemId] = (shipmentsArray.length - 1).toString();
    });
  });
  return {
    shipmentsArray,
    itemShipmentsDictionary,
  };
};

export const getTransactionType = (
  urlParameter: string | null,
  orderDetailsData: IOrderDetailsResponse | undefined,
): TransactionType => {
  if (urlParameter && urlParameter !== 'undefined') {
    // Some upstream sources send descriptive values like "SaleRering"/
    // "ReturnRering" instead of the plain "S"/"R" codes the API expects.
    if (urlParameter.startsWith('Return')) return 'R';
    if (urlParameter.startsWith('Sale')) return 'S';
    return String(urlParameter) as TransactionType;
  }
  if (orderDetailsData?.statusDescription === 'Returned') return 'R';
  return 'S';
};

export const buildOrderDetailsPath = (
  orderDetailsData: IOrderDetailsResponse,
  receiptOrderNumber?: string,
): string => {
  const {
    orderNumber,
    salesDate,
    storeNumber,
    transactionId,
    registerNumber,
    orderIndex,
  } = orderDetailsData;
  return `/myaccount/order-details?orderNumber=${
    receiptOrderNumber || orderNumber
  }&salesDate=${salesDate}&storeNumber=${storeNumber}&transactionId=${transactionId}&registerNumber=${registerNumber}&index=${orderIndex}`;
};

/**
 * Returns the set of fulfillment types that should receive Delivery Tracker
 * return enrichment, derived from the relevant feature switches. STH is gated by
 * `enableDeliveryTrackerReturnChanges`; BODFS/DFS by
 * `enableBodfsDfsSoris`. Both can be toggled independently.
 */
export const getDeliveryTrackerReturnTypes = (): FULFILLMENT_TYPE[] => {
  const types: FULFILLMENT_TYPE[] = [];
  if (FeatureSwitches.enableDeliveryTrackerReturnChanges) {
    types.push(FULFILLMENT_TYPE.STH);
  }
  if (FeatureSwitches.enableBodfsDfsSoris) {
    types.push(FULFILLMENT_TYPE.BODFS, FULFILLMENT_TYPE.DFS);
  }
  return types;
};

/**
 * Statuses where a fulfillment group legitimately has no delivery tracker data yet, so a
 * null tracker is expected rather than a failure (Start a Return is not force-hidden).
 * Scoped by fulfillment type because each type uses a different status vocabulary: STH only
 * 'Being Processed'; BODFS/DFS 'Processing'/'On Schedule'. Keeping them separate preserves
 * STH's prior semantics instead of widening its exemptions to the BODFS/DFS statuses.
 */
export const PRE_FULFILLMENT_STATUSES_BY_TYPE: Partial<
  Record<FULFILLMENT_TYPE, string[]>
> = {
  [FULFILLMENT_TYPE.STH]: [STATUS_DESCRIPTION.BEING_PROCESSED],
  [FULFILLMENT_TYPE.BODFS]: [
    STATUS_DESCRIPTION.PROCESSING,
    STATUS_DESCRIPTION.ON_SCHEDULE,
  ],
  [FULFILLMENT_TYPE.DFS]: [
    STATUS_DESCRIPTION.PROCESSING,
    STATUS_DESCRIPTION.ON_SCHEDULE,
  ],
};

/**
 * Predicate: is this fulfillment group gated by the delivery tracker for return
 * eligibility (i.e. its fulfillment type is in the feature-switch-derived set)?
 */
export const isTrackerGatedFG = (
  fulfillmentGroup: IFulfillmentGroup,
  trackerGatedReturnTypes: FULFILLMENT_TYPE[],
): boolean =>
  trackerGatedReturnTypes.includes(
    fulfillmentGroup?.fulfillmentType as FULFILLMENT_TYPE,
  );

/**
 * The fulfillment groups with a genuine Delivery Tracker failure: the tracker
 * came back null even though the order is past the pre-fulfillment phase.
 *
 * Returns the groups rather than a boolean so instrumentation can report the FG
 * that actually failed. On a mixed order the first tracker-gated FG is not
 * necessarily the failing one, so picking it produces attributes that describe
 * the wrong group.
 */
export const getFailedDeliveryTrackerFGs = (
  fulfillmentGroups?: IFulfillmentGroup[],
  trackerGatedReturnTypes: FULFILLMENT_TYPE[] = [],
): IFulfillmentGroup[] =>
  (fulfillmentGroups ?? []).filter((fg) => {
    if (!isTrackerGatedFG(fg, trackerGatedReturnTypes)) return false;
    if (fg.deliveryTrackerData !== null) return false;
    const preFulfillmentStatuses =
      PRE_FULFILLMENT_STATUSES_BY_TYPE[
        fg.fulfillmentType as FULFILLMENT_TYPE
      ] ?? [];
    return !preFulfillmentStatuses.includes(
      fg.lineItems?.[0]?.statusDescription ?? '',
    );
  });

// Helper function to see if any fulfillment group is eligible for returns
export const hasEligibleFGs = (fgs: IFulfillmentGroup[]) =>
  fgs.some(
    (fg) =>
      fg.actions?.returnOnlineAllowed ||
      fg.actions?.returnInStoreAllowed ||
      fg.actions?.returnLORPAllowed,
  );

/**
 * AND-gates a fulfillment group's OMS return-eligibility flags with the delivery
 * tracker's flags: a return type is allowed only when BOTH sources allow it.
 */
const crosscheckReturnActions = (
  fgActions: IFulfillmentGroup['actions'],
  trackerActions: IDeliveryTrackerData['actions'] | undefined,
) => ({
  returnOnlineAllowed:
    (fgActions?.returnOnlineAllowed ?? false) &&
    (trackerActions?.returnOnlineAllowed ?? false),
  returnInStoreAllowed:
    (fgActions?.returnInStoreAllowed ?? false) &&
    (trackerActions?.returnInStoreAllowed ?? false),
  returnLORPAllowed:
    (fgActions?.returnLORPAllowed ?? false) &&
    (trackerActions?.returnLORPAllowed ?? false),
});

/**
 * Separates delivery-tracker-eligible fulfillment groups from the rest and
 * crosschecks the eligible groups' OMS eligibility flags with their delivery
 * tracker eligibility flags using logical AND.
 *
 * This function processes fulfillment groups by:
 * 1. Separating eligible fulfillment groups (those whose fulfillment type is in
 *    `trackerGatedReturnTypes` — STH and/or BODFS/DFS depending on feature switches) from the rest
 * 2. For eligible groups: crosschecking OMS eligibility flags with delivery tracker
 *    eligibility flags using logical AND
 * 3. For other groups: returning them unchanged (no delivery tracker crosscheck needed)
 *
 * The crosscheck ensures that for eligible fulfillment groups, both OMS and delivery tracker
 * must allow a return type for it to be considered eligible. This prevents returns when
 * delivery tracker indicates issues with the shipment.
 *
 * @param fulfillmentGroups - Array of fulfillment groups from order details response
 * @param trackerGatedReturnTypes - Fulfillment types that receive the delivery tracker crosscheck
 * @returns Object containing:
 *   - enrichedFGs: Eligible fulfillment groups with crosschecked eligibility flags
 *   - otherFGs: Remaining fulfillment groups (unchanged)
 *
 * @example
 * ```typescript
 * const { enrichedFGs, otherFGs } = separateAndCrosscheckDeliveryTrackerFGs(
 *   orderData.fulfillmentGroups,
 *   getDeliveryTrackerReturnTypes(),
 * );
 * const hasEligibleReturns = hasEligibleFGs([...enrichedFGs, ...otherFGs]);
 * ```
 */
export const separateAndCrosscheckDeliveryTrackerFGs = (
  fulfillmentGroups: IFulfillmentGroup[] = [],
  trackerGatedReturnTypes: FULFILLMENT_TYPE[] = [],
) => {
  const { eligibleFGs, otherFGs } = fulfillmentGroups.reduce(
    (acc, fg) => {
      if (isTrackerGatedFG(fg, trackerGatedReturnTypes)) {
        acc.eligibleFGs.push(fg);
      } else {
        acc.otherFGs.push(fg);
      }
      return acc;
    },
    { eligibleFGs: [], otherFGs: [] } as {
      eligibleFGs: typeof fulfillmentGroups;
      otherFGs: typeof fulfillmentGroups;
    },
  );
  // Updated fulfillment group eligibilities by crosschecking with delivery tracker eligibility
  const enrichedFGs: IFulfillmentGroup[] = eligibleFGs.map((fg) => {
    const deliveryTrackerData = fg.deliveryTrackerData as IDeliveryTrackerData;
    return {
      ...fg,
      actions: {
        ...fg.actions,
        ...crosscheckReturnActions(fg.actions, deliveryTrackerData?.actions),
      },
    };
  });
  return { enrichedFGs, otherFGs };
};
